import { InsuranceType } from '@/utils/constants.js';
import { processAPIWithHeadersForPractitioners } from '@/utils/helpers.js';

type InsuranceTypeValue = (typeof InsuranceType)[keyof typeof InsuranceType];

/**
 * Raw body returned by the public chain-config list endpoint. The shape-check and
 * validation (`parseChainConfigs`) live in the SWR fetcher (`use-chain-configs.ts`), so this
 * service stays a thin transport.
 */
export interface ChainConfigsResponse {
    data?: unknown;
    meta?: unknown;
    [key: string]: unknown;
}

/**
 * Fetches the public, bookable appointment-chain configurations for a customer/location/insurance.
 *
 * Public route: only the `customer_id` header is sent (no `Authorization`). `insurance_type` must be
 * the uppercase `InsuranceType` (`formatInsuranceType()` output). `is_returning` is intentionally
 * omitted — patient-type gating is client-side, so the server returns both patient types.
 * Insurance type can be undefined, in which case the server will return all available configs.
 *
 * `processAPIWithHeadersForPractitioners` returns `res.json()` even on 4xx/5xx, so callers
 * distinguish empty vs error by response shape, not status.
 */
export const fetchPublicChainConfigs = async (
    customerId: string,
    locationKey: string,
    insuranceType?: InsuranceTypeValue
): Promise<ChainConfigsResponse> => {
    const params: Record<string, string> = {
        location_key: locationKey,
        ...(insuranceType && { insurance_type: insuranceType }),
    };
    const query = new URLSearchParams(params);

    return processAPIWithHeadersForPractitioners(
        `public/users/availability/appointment-chain-configs?${query.toString()}`,
        'GET',
        {
            headers: { customer_id: customerId },
        }
    );
};
